const PLAIN_NUMERIC_STRING = /^[+-]?(?:\d+\.?\d*|\.\d+)$/;

const SKIP_IDENTIFIER_TOKENS = new Set(["id"]);
const SKIP_YEAR_JOINED = new Set(["year", "school year"]);
const SKIP_GEO_OR_CONTACT_TOKENS = new Set([
  "zip",
  "zipcode",
  "postal",
  "latitude",
  "longitude",
  "lat",
  "lng",
  "lon",
  "phone",
]);

export interface FormatNumberOptions {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}

function parseNumericValue(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed === "" || trimmed.includes("%")) {
    return null;
  }

  const normalized = trimmed.replace(/,/g, "");
  if (!PLAIN_NUMERIC_STRING.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function columnNameTokens(columnName: string): string[] {
  return columnName
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((token) => token.toLowerCase());
}

/**
 * Formats a display metric with US thousands separators (1,000).
 * Accepts numbers or numeric strings (including already-grouped values).
 * Non-numeric input is returned unchanged so the helper is safe to wrap.
 */
export function formatNumber(value: unknown, options?: FormatNumberOptions): string {
  const parsed = parseNumericValue(value);
  if (parsed == null) {
    if (value == null || value === "" || (typeof value === "number" && !Number.isFinite(value))) {
      return "";
    }
    return String(value);
  }

  const maximumFractionDigits =
    options?.maximumFractionDigits ?? (Number.isInteger(parsed) ? 0 : 2);
  const minimumFractionDigits = options?.minimumFractionDigits ?? 0;

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(parsed);
}

/**
 * Columns whose cells are identifiers, calendar years, or geo/contact fields.
 * Projection year keys (`year_2024`) stay formattable because they hold counts.
 */
export function shouldSkipNumberFormat(columnName: string): boolean {
  const tokens = columnNameTokens(columnName);
  if (tokens.length === 0) {
    return false;
  }

  const joined = tokens.join(" ");
  if (SKIP_YEAR_JOINED.has(joined)) {
    return true;
  }

  if (
    tokens[tokens.length - 1] === "year" &&
    !tokens.some((token) => /^\d+$/.test(token))
  ) {
    return true;
  }

  if (SKIP_IDENTIFIER_TOKENS.has(tokens[tokens.length - 1])) {
    return true;
  }

  return tokens.some((token) => SKIP_GEO_OR_CONTACT_TOKENS.has(token));
}
